
import { memo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Cpu } from 'lucide-react';
import { AIProviderConfig } from './AIProviderConfig';

export const AiInsightConfig = memo(({ data, onChange, inputColumns = [] }: any) => {
    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c)).filter(Boolean);
    const autoTextField = colNames.find((c: string) => /extracted|merged|content|text|body/i.test(c)) || (colNames.length > 0 ? colNames[0] : '');
    const parseSelection = (value: any): string[] => {
        if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
        if (typeof value === 'string') return value.split(',').map((v) => v.trim()).filter(Boolean);
        return [];
    };
    const configuredTextFields = parseSelection(data.textField);
    const selectedTextFields = configuredTextFields.length > 0
        ? configuredTextFields
        : (autoTextField ? [autoTextField] : []);

    const toggleTextField = (column: string) => {
        const next = selectedTextFields.includes(column)
            ? selectedTextFields.filter((f: string) => f !== column)
            : [...selectedTextFields, column];
        onChange('textField', next.join(', '));
    };

    return (
        <div className="space-y-6">
            <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-800">
                <h4 className="font-semibold mb-1 flex items-center gap-1"><Cpu className="h-3 w-3" /> AI Insight</h4>
                <p>One-click analytics: sentiment, topics, extraction. No prompt engineering needed.</p>
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Analysis Type</Label>
                <Select
                    value={data.analysisType || 'Sentiment'}
                    onValueChange={(val) => onChange('analysisType', val)}
                >
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                        <SelectItem value="Sentiment">Sentiment Analysis</SelectItem>
                        <SelectItem value="Summary">Text Summarization</SelectItem>
                        <SelectItem value="Classification">Classification</SelectItem>
                        <SelectItem value="Extraction">Entity Extraction</SelectItem>
                        <SelectItem value="Topic Detection">Topic Detection</SelectItem>
                        <SelectItem value="Key Phrases">Key Phrase Extraction</SelectItem>
                        <SelectItem value="Risk Flag">Risk / Compliance Flag</SelectItem>
                        <SelectItem value="Custom">Custom Prompt</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Text Fields (Source)</Label>
                {colNames.length > 0 ? (
                    <div className="border border-gray-300 rounded-md bg-white p-2 max-h-40 overflow-auto space-y-1">
                        {colNames.map((col: string) => (
                            <label key={col} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectedTextFields.includes(col)}
                                    onChange={() => toggleTextField(col)}
                                    className="rounded border-gray-300"
                                />
                                <span>{col}</span>
                            </label>
                        ))}
                    </div>
                ) : (
                    <Input
                        type="text"
                        className="w-full text-sm"
                        value={selectedTextFields.join(', ')}
                        onChange={(e) => onChange('textField', e.target.value)}
                        placeholder="extracted_text, description"
                    />
                )}
                <p className="text-[10px] text-gray-500">
                    {colNames.length > 0
                        ? `Selected ${selectedTextFields.length} column(s). Combined for analysis.`
                        : 'Provide comma-separated column names containing text to analyze.'}
                </p>
                <div className="mt-2">
                    <Label className="text-[11px] text-gray-600">Manual Text Fields (comma-separated)</Label>
                    <Input
                        type="text"
                        className="w-full text-sm"
                        value={selectedTextFields.join(', ')}
                        onChange={(e) => onChange('textField', e.target.value)}
                        placeholder="projectName, taskName, description"
                    />
                </div>
            </div>

            {data.analysisType === 'Custom' && (
                <div className="space-y-3 animate-in fade-in">
                    <Label className="text-xs font-semibold text-gray-700 uppercase">Custom Prompt</Label>
                    <Textarea
                        className="text-xs min-h-[80px]"
                        placeholder="e.g. Analyze the mood of this text and extract key topics..."
                        value={data.customPrompt || ''}
                        onChange={(e) => onChange('customPrompt', e.target.value)}
                    />
                </div>
            )}

            <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Output Column</Label>
                <Input
                    type="text"
                    className="w-full text-sm"
                    value={data.outputColumn || 'ai_insight'}
                    onChange={(e) => onChange('outputColumn', e.target.value)}
                    placeholder="ai_insight"
                />
            </div>

            <AIProviderConfig data={data} onChange={onChange} />
        </div>
    );
});
